<template>
  <el-table
    ref="table"
    class="common-el-table-styles summary-el-table-styles"
    v-bind="$attrs"
    v-on="$listeners"
    :summary-method="getSummaries"
    empty-text="暂无符合条件数据"
  >
    <el-table-column v-if="showSelection" key="selection" type="selection" fixed="left" width="48"></el-table-column>
    <el-table-column key="dateDesc" show-overflow-tooltip label="日期" fixed="left" prop="dateDesc" width="90"> </el-table-column>
    <el-table-column
      v-for="col in innerCols"
      :key="col.key"
      :prop="col.key"
      :label="getTableLabel(col)"
      :fixed="col.fixed"
      :show-overflow-tooltip="col.overflow"
      :min-width="col.width"
      :align="col.align"
      :sortable="col.sortable"
      :sort-orders="['descending', 'ascending', null]"
    >
      <template slot-scope="{ row }">
        <template v-if="col.key === 'coverUrl'">
          <el-image
            v-if="['图片', '组图', '试玩素材'].includes(row.type)"
            v-popover="{ url: getRowImageUrlByType(row), type: 'image', height: 280 }"
            style="height: 60px; cursor: pointer"
            :src="getRowImageUrlByType(row)"
          ></el-image>

          <template v-else-if="row.type === '视频'">
            <el-image
              v-popover="{ url: row.videoUrl, type: 'video', height: row.width > row.height ? 280 : 420 }"
              style="height: 60px; cursor: pointer"
              :src="row.coverUrl"
            ></el-image>
          </template>
        </template>
        <div v-else-if="col.key === 'imageName'" style="white-space: pre">{{ row.imageName }}</div>
        <div v-else-if="col.key === 'imageId'">{{ dimension === SUB_MATERIAL ? row.imageDetailId : row.imageId }}</div>
        <div v-else-if="col.key === 'control'">
          <slot name="control" :row="row"></slot>
        </div>
        <span v-else-if="['pv', 'clickNum', 'clickUnitPrice', 'paperUseAmount', 'amount', 'newRoleMoney'].includes(col.key)">
          {{ toThousands(row[col.key]) }}
        </span>
        <span v-else-if="getPercent(col.key)">
          {{ toPercentFixed(row[col.key]) }}
        </span>
        <template v-else-if="col.key === 'imageStyleFullNames'">
          <p>{{ row.imageStyleFullNames }}</p>
        </template>
        <span v-else>{{ row[col.key] }}</span>
      </template>
    </el-table-column>
  </el-table>
</template>

<script setup>
import { ref, onUpdated, nextTick, watch, computed } from 'vue'
import { SOURCE_TYPES } from '@/views/material/tags/const'
import { toThousands, toPercentFixed } from '@/common/js/filters'
import { SUB_MATERIAL } from '../hooks/useAnalysisReport'
// eslint-disable-next-line
const props = defineProps({
  columns: {
    type: Array,
    default: () => []
  },
  summaries: {
    type: Object,
    default: () => ({})
  },
  dimension: {
    type: String,
    default: ''
  },
  showSelection: {
    type: Boolean,
    default: false
  }
})

const table = ref(null)

const innerCols = computed(() => {
  if (props.dimension === SUB_MATERIAL) return props.columns.filter((col) => !['7日消耗', '30日消耗'].includes(col.title))
  return props.columns
})

// Methods
const getPercent = (key) => {
  if (key && key.startsWith('createRoleRoi')) return true
  if (key && key.startsWith('createRole24hRoi')) return true
  return key ? key.indexOf('Rate') > -1 || key.indexOf('Retain') > -1 : false
}

watch(
  () => props.summaries,
  (v) => {
    nextTick(() => {
      table.value?.doLayout()
    })
  }
)

const getSummaries = (param) => {
  // 合计自定义
  const { columns } = param
  const sums = []

  columns.forEach((column, index) => {
    const prop = column.property
    if (column.type === 'selection') {
      sums[index] = ''
      return
    }
    if (prop === 'dateDesc') {
      sums[index] = '合计'
      return
    }
    const values = props.summaries[prop]
    if (typeof values === 'number' && getPercent(prop)) {
      sums[index] = toPercentFixed(values)
    } else if (typeof values === 'number') {
      sums[index] = toThousands(values)
    } else {
      sums[index] = values
    }
  })
  return sums
}

const getTableLabel = ({ title, key }) => {
  if (key === 'imageName') {
    return props.dimension === SUB_MATERIAL ? '子素材名称' : '母素材名称'
  }
  if (key === 'imageId') {
    return props.dimension === SUB_MATERIAL ? '子素材ID' : '母素材ID'
  }
  return title
}

const getRowImageUrlByType = (row) => {
  if (row.type === '图片') return row.coverUrl
  if (row.type === '试玩素材') return row.coverUrl

  // 母素材组图使用coverUrl字段、子素材使用overviewAddr
  if (row.type === '组图' && props.dimension !== SUB_MATERIAL) return row.coverUrl
  return row.overviewAddr
}

// Lifecycle
onUpdated(() => {
  nextTick(() => {
    table.value?.doLayout()
  })
})

// eslint-disable-next-line
defineExpose({
  clearSelection: () => table.value?.clearSelection()
})
</script>

<style lang="scss" scoped></style>
